import { DndContext, DragOverlay } from '@dnd-kit/core';
import { Box, GlobalStyles, useTheme } from '@mui/material';
import { CSSProperties, ReactNode } from 'react';
import ProductCardMedia from '../../../../../../components/Product/ProductCardMedia';
import { CatalogProductsDragAndDrop } from '../../hooks/useCatalogProductsDragAndDrop.types';
import CatalogProductListRowLeftBlock from '../../list/components/CatalogProductListRowLeftBlock';
import { anchorOverlayToPointerTopRight } from '../../utils/catalogDragOverlay.modifiers';

const LIST_OVERLAY_MODIFIERS = [anchorOverlayToPointerTopRight];

interface CatalogProductsDragAndDropBoundaryProps {
  dragAndDrop: CatalogProductsDragAndDrop;
  children: ReactNode;
}

const CatalogProductsDragAndDropBoundary = ({
  dragAndDrop,
  children,
}: CatalogProductsDragAndDropBoundaryProps) => {
  const theme = useTheme();

  if (!dragAndDrop.isEnabled) {
    return <>{children}</>;
  }

  const { activeItem } = dragAndDrop;

  const listRect = activeItem?.listRow?.rect;

  const overlayStyle: CSSProperties = {
    pointerEvents: 'none',
    ...(listRect
      ? {
          boxSizing: 'border-box',
          width: listRect.width,
          height: listRect.height,
          overflow: 'hidden',
          borderRadius: theme.shape.borderRadiusLg,
          backgroundColor: theme.palette.background.paper,
        }
      : { height: 'auto' }),
  };

  const renderOverlayContent = () => {
    if (activeItem?.listRow) {
      return (
        <CatalogProductListRowLeftBlock
          {...activeItem.listRow.leftBlock}
          onOpenDetails={undefined}
        />
      );
    }

    if (activeItem?.productCard) {
      const { item, catalogId } = activeItem.productCard;

      return (
        <Box
          sx={{
            overflow: 'hidden',
            borderRadius: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <ProductCardMedia
            catalogId={catalogId}
            bottomTags={null}
            medias={item.medias}
            entityRelations={item.entityRelations}
          />
        </Box>
      );
    }

    return null;
  };

  return (
    <DndContext {...dragAndDrop.dragAndDropContextProps}>
      {activeItem && <GlobalStyles styles={{ '*': { cursor: 'grabbing !important' } }} />}

      {children}
      <DragOverlay
        dropAnimation={null}
        style={overlayStyle}
        modifiers={listRect ? LIST_OVERLAY_MODIFIERS : undefined}
      >
        {renderOverlayContent()}
      </DragOverlay>
    </DndContext>
  );
};

export default CatalogProductsDragAndDropBoundary;
